/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    createLogPrefix,
    logDebug,
    type LogTarget,
    readResponseBody,
    requestWithRetry,
} from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import type { AuthTokenProvider, CsrfToken } from '@amzn/vinyl-amzn'
import { type Auth, AuthStatus } from '@/auth/auth'
import { logStatusTransition } from '@/auth/authLog'
import {
    type BootConfig,
    type BootSession,
    type CsrfConfig,
    EXPIRY_BUFFER_MS,
    readInlineBootConfig,
    toBootSession,
    type TokenConfig,
} from '@/auth/bootConfig'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

// Vite `define` bakes `__DEV__` at build time; read defensively since the
// symbol is undeclared outside a Vite build.
declare const __DEV__: boolean | undefined

// AuthPortal is cookie-session based; the boot token is served by the same
// origin under `/config.json`, refreshed via `/pandaToken`. `/config.json` MUST
// be POSTed: AuthPortal only returns the `csrf` triple (needed to authenticate
// cookie-session gql/DMLS requests) on a POST — a GET omits it, so the session
// can't be validated downstream (Firefly 401s AUTH_INVALID_CUSTOMER_ID). The
// session-scoped csrf then persists across `/pandaToken` refreshes, which stay
// GET (that endpoint 404s on POST and returns only the token, no csrf).
const CONFIG_PATH = '/config.json?clientApplication=bullfrog'
const PANDA_TOKEN_PATH = '/pandaToken'
const POST: RequestInit = { method: 'POST' }

// Refresh a still-valid token this far ahead of expiry so an in-flight request
// never carries a token that lapses mid-request; only block on the refresh once
// the token is inside the hard-expiry window.
const REFRESH_BUFFER_MS = 3 * 60 * 1000

// GH sends an account that hasn't picked a listener profile here before anything
// else; AuthPortal withholds the session's access token until one is chosen.
const WHO_IS_LISTENING_PATH = '/identity/who-is-listening'

interface PandaTokenResponse {
    accessToken?: string
    expiresIn?: number
    csrf?: CsrfConfig
    montanaCsrf?: string
}

/**
 * Server-session (AuthPortal) auth: no client secret and no OAuth popup. The
 * signed-in session lives in a first-party cookie; sign-in / sign-out are
 * full-page navigations to AuthPortal's `/forceSignIn` / `/forceSignOut`, and
 * the token is never persisted to localStorage.
 */
export class AuthPortalAuth implements Auth, LogTarget {
    logPrefix = createLogPrefix('AuthPortalAuth')

    private _status$ = data(AuthStatus.PENDING)
    private _tokenConfig: TokenConfig | null = null
    private _csrf: CsrfConfig | null = null
    private _montanaCsrf: string | null = null
    private _customerId: string | null = null
    private _loadingPromise: Promise<void>
    private _refreshPromise: Promise<void> | null = null
    private errorHandler = createErrorHandler(this)

    // A signed-in session authenticates with the first-party cookie, so it must
    // travel. Signed out, browse is anonymous and needs no cookie, and leftover
    // retail cookies naming a customer this session can't vouch for make Firefly
    // reject the anonymous request (AUTH_INVALID_CUSTOMER_ID).
    get requestCredentials(): RequestCredentials {
        return this.isSignedIn() ? 'include' : 'omit'
    }

    constructor() {
        const inlineSession = readInlineBootConfig()
        if (inlineSession) {
            // Settled before the first paint: status never sits on PENDING, so
            // nothing downstream (router navigation, gql) waits on the network.
            logDebug(this, 'boot config read from the served HTML')
            this._loadingPromise = Promise.resolve()
            this.applySession(inlineSession)
            return
        }
        // A failed boot load settles as signed-out rather than leaving status
        // stuck on PENDING; the rejection is surfaced through the error handler.
        this._loadingPromise = this.loadBootConfig().catch((error) => {
            this.setStatus(AuthStatus.SIGNED_OUT)
            this.errorHandler(error)
        })
    }

    private setStatus = (status: AuthStatus) => logStatusTransition(this, this._status$, status)

    get status(): ObservableValue<AuthStatus> {
        return this._status$
    }

    // The cookie session is the credential; the access token is optional (GH's
    // `isAuthenticated` is `!!customerId`), only cross-origin services need it.
    isSignedIn(): boolean {
        return this._customerId != null
    }

    // Cookie-session auth: the request is authenticated by the first-party
    // session cookie + CSRF triple, not a bearer token, so the gql layer must
    // NOT attach an Authorization header (a bearer alongside the cookie 401s).
    usesBearer(): boolean {
        return false
    }

    get authTokenProvider(): AuthTokenProvider {
        return this.provideToken
    }

    // DMLS's `authCookie` accepts a `() => Promise<true>` freshness gate; the
    // stream awaits it before each request so it never fires against an
    // about-to-lapse cookie session. Only cookie-session controllers expose one.
    getAuthCookieProvider(): () => Promise<true> {
        return this.provideCookieFreshness
    }

    signIn(): void {
        logDebug(this, 'signIn')
        window.location.href = `https://${this.host}/forceSignIn`
    }

    signOut(): void {
        logDebug(this, 'signOut')
        window.location.href = `https://${this.host}/forceSignOut`
    }

    // Degrade gracefully when the boot payload carried no CSRF triple (shape
    // unconfirmed for some sessions): return undefined rather than throw, so
    // the request still goes out cookie-authenticated.
    getCsrfHeaders(): Record<string, string> | undefined {
        if (!this._csrf) return undefined
        return {
            'csrf-rnd': this._csrf.rnd,
            'csrf-token': this._csrf.token,
            'csrf-ts': this._csrf.ts,
        }
    }

    // The player/DMLS path (DmlsOptions.csrfTokenProvider) wants the CSRF triple
    // as a `CsrfToken` object, not headers; the stored `_csrf` already matches
    // that `{token, rnd, ts}` shape. Undefined when the session carried none, so
    // the stream still goes out cookie-authenticated.
    getCsrfToken(): CsrfToken | undefined {
        return this._csrf ?? undefined
    }

    // Boot payload carries the signed-in customerId; null until the boot config
    // has loaded, or when the session is signed-out.
    getCustomerId(): string | null {
        return this._customerId
    }

    // The Audible SDK's LPH endpoint wants the Montana CSRF, a distinct token
    // from the CSRF triple. Undefined when the session carried none (anon /
    // pre-boot), so the SDK config falls back to `''`.
    getMontanaCsrf(): string | undefined {
        return this._montanaCsrf ?? undefined
    }

    // AuthPortal requires HTTPS; a dev server started on `http:` is force-
    // redirected to `https:` before any token endpoint is contacted. Swapping
    // the protocol keeps the path, query and hash, so the reload lands on the
    // same route with the flags (`?authPortal=1`) that selected this controller.
    private get host(): string {
        if (typeof __DEV__ !== 'undefined' && __DEV__ && window.location.protocol === 'http:') {
            window.location.protocol = 'https:'
        }
        return window.location.host
    }

    private loadBootConfig = async (): Promise<void> => {
        logDebug(this, 'loadBootConfig')
        const response = await requestWithRetry(CONFIG_PATH, POST)
        const config: BootConfig = await readResponseBody(response, 'json')
        this.applySession(toBootSession(config, Date.now()))
    }

    // AuthPortal mints the csrf triple for anonymous sessions too (GH sends it
    // either way), and the customerId alone decides the sign-in state.
    private applySession(session: BootSession): void {
        if (session.csrf) this._csrf = session.csrf
        if (session.montanaCsrf) this._montanaCsrf = session.montanaCsrf
        if (session.token) {
            this._tokenConfig = session.token
            // Presence + expiry only — never the token value.
            const expiresIn = Math.round((session.token.expiresAt - Date.now()) / 1000)
            logDebug(this, `boot token loaded, expires in ${expiresIn}s`)
        }
        if (!session.customerId) {
            this.setStatus(AuthStatus.SIGNED_OUT)
            return
        }
        this._customerId = session.customerId
        if (session.needsProfilePicker) redirectToProfilePicker()
        this.setStatus(AuthStatus.SIGNED_IN)
    }

    private provideToken = async (): Promise<string> => {
        const { accessToken } = await this.ensureFresh()
        return accessToken
    }

    // The session cookie is kept alive by the same `/pandaToken` refresh that
    // mints the token, so token expiry doubles as the cookie's freshness signal.
    // Resolves `true` only once fresh.
    private provideCookieFreshness = async (): Promise<true> => {
        await this._loadingPromise
        if (!this._customerId) throw new Error('not signed in')
        // No token means nothing to refresh: the cookie alone authorizes the stream.
        if (this._tokenConfig) await this.ensureFresh()
        return true
    }

    // Blocks until the session is fresh: refreshes ahead of expiry (in the
    // background while still valid, awaited once inside the hard-expiry window)
    // and coalesces concurrent callers onto a single `/pandaToken` call.
    private ensureFresh = async (): Promise<TokenConfig> => {
        await this._loadingPromise
        if (!this._customerId) throw new Error('not signed in')
        if (!this._tokenConfig) throw new Error('this cookie session has no access token')
        const { expiresAt } = this._tokenConfig
        if (expiresAt < Date.now() + REFRESH_BUFFER_MS) {
            const refresh = this.getRefreshPromise()
            if (expiresAt < Date.now() + EXPIRY_BUFFER_MS) {
                await refresh
            } else {
                // Not awaited — the current token is still good — but a failure
                // here is the first sign the session is dying, so report it
                // rather than let the session lapse silently.
                refresh.catch(this.errorHandler)
            }
        }
        // Never hand a lapsed session downstream — it pushes bad auth to the wire.
        if (this._tokenConfig.expiresAt < Date.now()) {
            throw new Error('session refresh failed; no fresh access token available')
        }
        return this._tokenConfig
    }

    // Coalesce concurrent refreshes onto a single in-flight `/pandaToken` call.
    private getRefreshPromise = (): Promise<void> => {
        if (!this._refreshPromise) {
            this._refreshPromise = this.refreshToken().finally(() => {
                this._refreshPromise = null
            })
        }
        return this._refreshPromise
    }

    private refreshToken = async (): Promise<void> => {
        const response = await requestWithRetry(`https://${this.host}${PANDA_TOKEN_PATH}`)
        const token: PandaTokenResponse = await readResponseBody(response, 'json')
        if (!token.accessToken || token.expiresIn == null) {
            throw new Error('pandaToken response did not contain an access token')
        }
        this._tokenConfig = {
            accessToken: token.accessToken,
            expiresAt: Date.now() + token.expiresIn * 1000,
        }
        // Presence + expiry only — never the token value.
        logDebug(this, `token refreshed, expires in ${token.expiresIn}s`)
        // Keep the CSRF triple fresh when the refresh mints a new one; a
        // refresh that omits it leaves the boot triple in place.
        if (token.csrf) this._csrf = token.csrf
        // Same for the Montana CSRF; refresh only overwrites when it carries one.
        if (token.montanaCsrf) this._montanaCsrf = token.montanaCsrf
    }
}

function redirectToProfilePicker(): void {
    const { pathname, search } = window.location
    if (pathname.startsWith('/identity/')) return
    const returnTo =
        pathname !== '/' || search ? `?returnTo=${encodeURIComponent(pathname + search)}` : ''
    window.location.href = `${WHO_IS_LISTENING_PATH}${returnTo}`
}
